// @flow
import { Helmet } from "react-helmet";
import styled, { ThemeProvider } from "styled-components";
import { withCms, renderSlices } from "@nested/react-with-cms";
import {
  theme as oldTheme,
  Section,
  Div,
  H3,
  P,
  media,
} from "@nested/nested-react-components";
import { formatPrice } from "@nested/utils";
import { HeroTitle } from "../../components/HeroTitle/HeroTitle";
import { HeroSubtitle } from "../../components/HeroSubtitle/HeroSubtitle";
import { HeroDetails } from "../../components/HeroDetails/HeroDetails";
import * as slices from "./slices";
import { StyledHero } from "./StyledHero";

const Fact = styled(P)`
  color: ${({ theme }) => theme.colors.greys.white};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.font.bold};

  span {
    font-size: 20px;
  }

  ${media.sm`
    span {
      font-size: 28px;
    }
  `};
`;

const HeroFacts = styled(Div)`
  max-width: ${({ theme }) => theme.font.typographyBreakpoint};

  p {
    text-align: center;
    padding-right: 10px;

    &:last-child {
      padding-right: 0;
    }
    & + p {
      padding-left: 10px;
    }
  }
`;

const ArticleStyle = styled(Section)`
  margin-top: calc(${({ theme }) => theme.padding.base} * 10);
  padding-bottom: 50px;
  * {
    max-width: ${({ theme }) => theme.font.typographyBreakpoint};
    margin-left: auto;
    margin-right: auto;
  }
  h3,
  p {
    margin-bottom: ${({ theme }) => theme.padding.base};

    &:last-child {
      margin-bottom: 0;
    }
  }
  blockquote {
    margin-left: 0;
    margin-right: 0;
  }
`;

const Article = (props) => <ArticleStyle {...props} as="article" />;

type Props = {
  metaTitle?: string,
  metaDescription?: string,
  customer: string,
  property: string,
  area: string,
  receivedOffer: string,
  finalSalePrice: string,
  body: any[],
  heroBackgroundImage: string,
  storyTitle: string,
};

export const CustomerStory = ({
  metaTitle,
  metaDescription,
  customer,
  storyTitle,
  property,
  area,
  receivedOffer,
  finalSalePrice,
  body = [],
  heroBackgroundImage,
}: Props) => (
  <ThemeProvider theme={oldTheme}>
    <div>
      <Helmet>
        {metaTitle && <title>{metaTitle}</title>}
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
      </Helmet>
      <StyledHero colour="white" heroImage={heroBackgroundImage}>
        {customer && <HeroTitle white>{customer}</HeroTitle>}
        {property && area && (
          <HeroSubtitle white bold>{`${property} - ${area}`}</HeroSubtitle>
        )}
        <HeroDetails>
          <HeroFacts fixed row align="center" justify="space-between">
            {receivedOffer && (
              <Fact>
                Received an offer in
                <br />
                <span>{`${receivedOffer} days`}</span>
              </Fact>
            )}
            {finalSalePrice && (
              <Fact>
                Final sale price
                <br />
                <span>{formatPrice(finalSalePrice)}</span>
              </Fact>
            )}
          </HeroFacts>
        </HeroDetails>
      </StyledHero>
      <Article fixed align="center">
        <H3>{storyTitle}</H3>
        {body && renderSlices(body, slices)}
      </Article>
    </div>
  </ThemeProvider>
);

export const UniversalRender = withCms({
  template: "customer-story",
})(CustomerStory);
